import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { fetchCurrentTenant, useAuth, usePermission } from '@saas-kit/lib';
import {
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  PageHeader,
  Spinner,
} from '@saas-kit/ui';

export function HomePage(): JSX.Element {
  const { t } = useTranslation('home');
  const user = useAuth((s) => s.user);
  const selectedTenant = useAuth((s) => s.selectedTenant);
  const tenants = useAuth((s) => s.tenants);
  const canInvite = usePermission('members.invite');
  const canManageMembers = usePermission('members.manage');
  const canReadFiles = usePermission('files.read');
  const canManageApiKeys = usePermission('api_keys.manage');
  const query = useQuery({
    queryKey: ['tenants', 'current', selectedTenant?.tenant_id],
    queryFn: fetchCurrentTenant,
    enabled: Boolean(selectedTenant?.tenant_id),
  });
  const tenant = query.data ?? selectedTenant;
  const permissionCount = user?.effective_permissions.length ?? 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('title')}
        description={
          tenant ? t('subtitle', { tenant: tenant.name }) : t('subtitle_no_tenant')
        }
      />

      <div className="grid gap-4 md:grid-cols-3">
        <MetricCard label={t('metrics.workspace_status')} value={tenant?.status ?? '—'} />
        <MetricCard label={t('metrics.workspaces')} value={String(tenants.length)} />
        <MetricCard label={t('metrics.permissions')} value={String(permissionCount)} />
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)]">
        <Card>
          <CardHeader>
            <CardTitle>{t('tenant_summary_title')}</CardTitle>
            <CardDescription>{t('description')}</CardDescription>
          </CardHeader>
          <CardContent>
            {selectedTenant && query.isLoading ? (
              <Spinner size="sm" />
            ) : tenant ? (
              <dl className="grid gap-3 text-sm md:grid-cols-2">
                <SummaryRow label={t('tenant_name_label')} value={tenant.name} />
                <SummaryRow label={t('tenant_slug_label')} value={tenant.slug ?? '—'} />
                <SummaryRow
                  label={t('tenant_status_label')}
                  value={<Badge>{tenant.status}</Badge>}
                />
                <SummaryRow label={t('user_label')} value={user?.display_name ?? user?.email ?? '—'} />
                <SummaryRow label={t('email_label')} value={user?.email ?? '—'} />
              </dl>
            ) : tenants.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('tenant_empty')}</p>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('quick_actions.title')}</CardTitle>
            <CardDescription>{t('quick_actions.description')}</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3">
            <QuickLink
              to="/team"
              title={t('quick_actions.members_title')}
              body={t('quick_actions.members_body')}
            />
            {canInvite ? (
              <QuickLink
                to="/team/invitations"
                title={t('quick_actions.invitations_title')}
                body={t('quick_actions.invitations_body')}
              />
            ) : null}
            {canReadFiles ? (
              <QuickLink
                to="/files"
                title={t('quick_actions.files_title')}
                body={t('quick_actions.files_body')}
              />
            ) : null}
            {canManageMembers ? (
              <QuickLink
                to="/team/roles"
                title={t('quick_actions.roles_title')}
                body={t('quick_actions.roles_body')}
              />
            ) : null}
            {canManageApiKeys ? (
              <QuickLink
                to="/api-keys"
                title={t('quick_actions.api_keys_title')}
                body={t('quick_actions.api_keys_body')}
              />
            ) : null}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: ReactNode }): JSX.Element {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="text-foreground">{value}</dd>
    </div>
  );
}

function MetricCard({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <Card>
      <CardContent className="p-5">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="mt-2 text-2xl font-semibold text-foreground">{value}</p>
      </CardContent>
    </Card>
  );
}

function QuickLink({
  to,
  title,
  body,
}: {
  to: string;
  title: string;
  body: string;
}): JSX.Element {
  return (
    <Link
      to={to}
      className="rounded-lg border border-border bg-muted/30 px-4 py-3 transition-colors hover:bg-muted"
    >
      <p className="text-sm font-medium text-foreground">{title}</p>
      <p className="mt-1 text-sm text-muted-foreground">{body}</p>
    </Link>
  );
}
